import { commonVar } from "../providers/common-var/common";

export default class PhoneUtils {
  /**
   * Removes the phone number mask from the supplied number and returns a new one
   * @param n the number to be de-masked
   */
  public static removePhoneMask(n: string): string {
    if (!n) {
      return n;
    } else {
      return n.replace(/\D+/g, "");
    }
  }

  /**
   * Adds a mask to a supplied phone number
   * @param number the number to be masked
   */
  public static applyPhoneMask(number: string): string {
    if (!number) {
      return number;
    }

    let numbers = number.split("");
    let lastIndex = 0;
    let mask = commonVar.PHONE_US_MASK.split("");
    mask.forEach((val, index) => {
      if (commonVar.SPECIAL_CHAR.indexOf(val) === -1) {
        mask[index] = numbers[lastIndex];
        lastIndex += 1;
      }
    });

    return mask.join("");
  }

  /**
   * Normalizes search input for phone number searches.
   * If the input looks like a phone number (contains mostly digits with common
   * phone formatting characters), strips all non-numeric characters.
   * Otherwise, returns the input unchanged for text-based searches.
   *
   * @param input the search input string
   * @returns normalized string (digits only if phone-like) or original input
   *
   * Examples:
   * - "(555) 123-4567" → "5551234567"
   * - "555-123-4567" → "5551234567"
   * - "555.123.4567" → "5551234567"
   * - "555 123 4567" → "5551234567"
   * - "555-123" → "555123"
   * - "5551234567" → "5551234567"
   * - "Blue Cross" → "Blue Cross" (unchanged)
   * - "Medicare ABC" → "Medicare ABC" (unchanged)
   * - "ABC-123" → "ABC-123" (unchanged - has letters)
   * - "" → ""
   * - null → null
   */
  public static normalizeSearchInputForPhoneNumbers(input: string): string {
    if (!input) {
      return input;
    }

    // Extract only digits from the input
    const digitsOnly = input.replace(/\D/g, "");

    // If no digits, return original (pure text search)
    if (digitsOnly.length === 0) {
      return input;
    }

    // Count non-digit characters
    const nonDigitChars = input.replace(/\d/g, "");

    // If input has no non-digit characters, it's already normalized
    if (nonDigitChars.length === 0) {
      return input;
    }

    // Check if all non-digit characters are phone formatting characters
    // Phone formatting chars: ( ) - . and whitespace
    const phoneFormattingCharsOnly = nonDigitChars.replace(/[\(\)\-\.\s]/g, "");

    // If there are letters or other non-phone characters, return original
    // This prevents normalizing mixed alphanumeric like "ABC-123" or "Medicare Plan 1"
    if (phoneFormattingCharsOnly.length > 0) {
      return input;
    }

    // At this point, input contains only digits and phone formatting characters
    // Check if it looks like a reasonable phone number length (3-11 digits)
    // 3 digits = area code only, 11 digits = 1 + 10 digit US number
    if (digitsOnly.length >= 3 && digitsOnly.length <= 11) {
      return digitsOnly;
    }

    // If digit count is outside phone number range, return original
    return input;
  }
}



// WEBPACK FOOTER //
// ./src/utils/phone.utils.ts